import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { garAxios } from "./garApi";
import { money } from "./api";
import useVaipedeManifest from "./useVaipedeManifest";
import { LogOut, UtensilsCrossed, ChefHat, Bell, Users, Plus, Minus, X, Trash2, Check, Search, Clock } from "lucide-react";

const NOTIF_KEY = "vp_garcom_notif_seen";

/** Cronômetro em tempo real do pedido na cozinha (mm:ss). */
function GarcomTimer({ startIso }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const iv = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(iv);
  }, []);
  let start = 0;
  try { start = new Date(startIso).getTime() || 0; } catch { start = 0; }
  if (!start) return null;
  const totalSec = Math.max(0, Math.floor((now - start) / 1000));
  const mm = Math.floor(totalSec / 60);
  const ss = totalSec % 60;
  const label = `${String(mm).padStart(2, "0")}:${String(ss).padStart(2, "0")}`;
  let cls = "bg-emerald-50 text-emerald-700 border border-emerald-200";
  let anim = "";
  if (mm >= 20) { cls = "bg-red-100 text-red-700 border border-red-300"; anim = "animate-pulse"; }
  else if (mm >= 10) { cls = "bg-amber-100 text-amber-800 border border-amber-300"; }
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-black px-2 py-0.5 rounded-full ${cls} ${anim}`} title="Tempo do pedido">
      <Clock className="w-3 h-3"/> {label}
    </span>
  );
}

export default function VaiPedeGarcomDashboardPage() {
  useVaipedeManifest();
  const navigate = useNavigate();
  const [tab, setTab] = useState("mesas"); // mesas | cozinha
  const [loja, setLoja] = useState(null);
  const [garcom, setGarcom] = useState(null);
  const [mesas, setMesas] = useState([]);
  const [cozinha, setCozinha] = useState([]);
  const [notif, setNotif] = useState([]);
  const [notifSeen, setNotifSeen] = useState(() => new Set(JSON.parse(localStorage.getItem(NOTIF_KEY) || "[]")));
  const [openComanda, setOpenComanda] = useState(null);
  const audioRef = useRef(null);

  useEffect(() => {
    const g = localStorage.getItem("vaipede_garcom");
    if (!g) { navigate("/vaipede/garcom/login"); return; }
    setGarcom(JSON.parse(g));
    garAxios.get("/vaipede/garcom/loja").then(r => setLoja(r.data)).catch(() => {});
  }, [navigate]);

  const loadMesas = () => garAxios.get("/vaipede/garcom/mesas").then(r => setMesas(r.data)).catch(() => {});
  const loadCozinha = () => garAxios.get("/vaipede/garcom/cozinha").then(r => setCozinha(r.data)).catch(() => {});
  const loadNotif = () => garAxios.get("/vaipede/garcom/notificacoes").then(r => setNotif(r.data)).catch(() => {});

  useEffect(() => { loadMesas(); loadCozinha(); loadNotif(); }, []);
  useEffect(() => {
    const t = setInterval(() => { loadMesas(); loadCozinha(); loadNotif(); }, 15000);
    return () => clearInterval(t);
  }, []);

  // Notificações de pedido pronto (som + toast)
  useEffect(() => {
    const novos = notif.filter(p => !notifSeen.has(p.id));
    if (novos.length > 0) {
      try { audioRef.current?.play(); } catch {}
      novos.forEach(p => toast(`🍽️ Pedido #${p.numero} PRONTO${p.mesa_numero ? ` · Mesa ${p.mesa_numero}` : ""}`, { duration: 8000 }));
      const nova = new Set([...notifSeen, ...novos.map(p => p.id)]);
      setNotifSeen(nova);
      localStorage.setItem(NOTIF_KEY, JSON.stringify(Array.from(nova)));
    }
  }, [notif]);

  const logout = () => {
    localStorage.removeItem("vaipede_garcom_token");
    localStorage.removeItem("vaipede_garcom");
    navigate("/vaipede/garcom/login");
  };

  if (!garcom) return null;
  const prontosNaoVistos = notif.filter(p => !notifSeen.has(p.id)).length;

  return (
    <div className="min-h-screen bg-neutral-50 pb-24" data-testid="vaipede-garcom-dashboard">
      {/* Áudio de notificação (data URI simples de beep curto) */}
      <audio ref={audioRef} preload="auto" src="data:audio/wav;base64,UklGRl9vAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YT8AAAAA////gACAAP///4CAgP///4CAgP///4CAgP///4CAgP///4CAgP///4CAgP///4CAgP///4CAgP///4CAgA=="/>

      <header className="bg-[#EA1D2C] text-white p-4 sticky top-0 z-30">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <div className="min-w-0">
            <div className="text-xs opacity-80">{loja?.loja_nome || "VAI PEDE"}</div>
            <h1 className="font-black text-lg truncate">Olá, {garcom.nome}</h1>
          </div>
          <div className="flex items-center gap-2">
            {notif.length > 0 && (
              <div className="relative">
                <Bell className="w-6 h-6"/>
                <span className="absolute -top-1 -right-1 bg-yellow-300 text-red-700 text-[10px] font-black rounded-full w-4 h-4 grid place-items-center">{notif.length}</span>
              </div>
            )}
            <button onClick={logout} className="p-2 rounded-lg hover:bg-white/10" data-testid="vaipede-garcom-logout">
              <LogOut className="w-5 h-5"/>
            </button>
          </div>
        </div>
      </header>

      {/* Banner de notificações — pedidos prontos */}
      {notif.length > 0 && (
        <div className="max-w-3xl mx-auto px-4 pt-3">
          <div className="bg-emerald-50 border-2 border-emerald-300 rounded-2xl p-3">
            <div className="text-xs font-black text-emerald-800 mb-1">🍽️ PEDIDOS PRONTOS PARA SERVIR</div>
            <div className="flex flex-wrap gap-1">
              {notif.map(p => (
                <span key={p.id} className="bg-emerald-600 text-white text-xs font-bold px-2 py-1 rounded-lg" data-testid={`vaipede-garcom-notif-${p.id}`}>
                  #{p.numero}{p.mesa_numero ? ` · Mesa ${p.mesa_numero}` : ""}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="max-w-3xl mx-auto px-4 pt-3">
        <div className="flex bg-white rounded-full p-1 border border-neutral-200">
          <button onClick={() => setTab("mesas")} className={`flex-1 py-2 rounded-full text-sm font-black transition ${tab === "mesas" ? "bg-[#EA1D2C] text-white" : "text-neutral-500"}`} data-testid="vaipede-garcom-tab-mesas">
            <Users className="w-4 h-4 inline mr-1"/> Mesas
          </button>
          <button onClick={() => setTab("cozinha")} className={`flex-1 py-2 rounded-full text-sm font-black transition relative ${tab === "cozinha" ? "bg-[#EA1D2C] text-white" : "text-neutral-500"}`} data-testid="vaipede-garcom-tab-cozinha">
            <ChefHat className="w-4 h-4 inline mr-1"/> Cozinha
            {prontosNaoVistos > 0 && (
              <span className="absolute -top-1 -right-1 bg-yellow-400 text-red-700 text-[10px] font-black rounded-full min-w-[18px] h-[18px] grid place-items-center px-1 animate-pulse">{prontosNaoVistos}</span>
            )}
          </button>
        </div>
      </div>

      <main className="max-w-3xl mx-auto p-4">
        {tab === "mesas" && (
          <div className="grid sm:grid-cols-2 gap-3">
            {mesas.length === 0 && <p className="text-center text-neutral-500 col-span-full py-8">Nenhuma mesa cadastrada.</p>}
            {mesas.map(m => (
              <div key={m.id} className={`bg-white rounded-2xl border-2 ${m.ocupada ? "border-[#EA1D2C]" : "border-neutral-200"} p-4`} data-testid={`vaipede-garcom-mesa-${m.id}`}>
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <div className="font-black text-lg">🪑 {m.nome || `Mesa ${m.numero}`}</div>
                    <div className="text-xs text-neutral-500">{m.capacidade || 1} pessoa{(m.capacidade || 1) > 1 ? "s" : ""}</div>
                  </div>
                  {m.ocupada && (
                    <div className="text-right">
                      <div className="text-xs text-neutral-500">Aberto</div>
                      <div className="font-black text-[#EA1D2C]">{money(m.total_aberto)}</div>
                    </div>
                  )}
                </div>
                <button onClick={() => setOpenComanda(m)} className="w-full py-2 rounded-xl bg-neutral-900 text-white font-bold text-sm" data-testid={`vaipede-garcom-abrir-${m.id}`}>
                  Abrir comanda
                </button>
              </div>
            ))}
          </div>
        )}

        {tab === "cozinha" && (
          <div className="space-y-3">
            {cozinha.length === 0 && <p className="text-center text-neutral-500 py-8">Sem pedidos na cozinha.</p>}
            {cozinha.map(p => (
              <div key={p.id} className={`bg-white rounded-2xl border-2 p-4 ${p.status === "pronto" ? "border-emerald-500" : "border-amber-400"}`} data-testid={`vaipede-garcom-cozinha-${p.id}`}>
                <div className="flex items-center justify-between mb-2 gap-2">
                  <div className="min-w-0">
                    <b>#{p.numero}</b>{p.mesa_numero ? ` · Mesa ${p.mesa_numero}` : ` · ${p.tipo}`}
                    <div className="text-xs text-neutral-500">{new Date(p.created_at).toLocaleTimeString("pt-BR")}</div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <GarcomTimer startIso={p.created_at}/>
                    <span className={`text-xs font-black px-2 py-1 rounded-full ${p.status === "pronto" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                      {p.status === "pronto" ? "🍽️ PRONTO" : "🔥 Preparando"}
                    </span>
                  </div>
                </div>
                <div className="text-sm space-y-0.5">
                  {(p.itens || []).map((it, i) => (
                    <div key={i} className="flex justify-between">
                      <span>{it.quantidade}x {it.nome}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {openComanda && (
        <AbrirComandaModal
          mesa={openComanda}
          onClose={() => { setOpenComanda(null); loadMesas(); loadCozinha(); }}
        />
      )}
    </div>
  );
}

function AbrirComandaModal({ mesa, onClose }) {
  const [produtos, setProdutos] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [busca, setBusca] = useState("");
  const [carrinho, setCarrinho] = useState({});
  const [clienteNome, setClienteNome] = useState("");
  const [observacao, setObservacao] = useState("");
  const [pessoas, setPessoas] = useState(mesa.capacidade || 1);
  const [salvandoPessoas, setSalvandoPessoas] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [showCart, setShowCart] = useState(false);

  useEffect(() => {
    garAxios.get("/vaipede/garcom/produtos").then(r => setProdutos(r.data));
    garAxios.get("/vaipede/garcom/categorias").then(r => setCategorias(r.data));
  }, []);

  const filtrados = produtos.filter(p => p.nome.toLowerCase().includes(busca.toLowerCase()));
  const grouped = useMemo(() => {
    const nomes = categorias.map(c => c.nome);
    const nomesSet = new Set(nomes);
    filtrados.forEach(p => { if (p.categoria && !nomesSet.has(p.categoria)) { nomes.push(p.categoria); nomesSet.add(p.categoria); }});
    if (!nomesSet.has("Outros")) nomes.push("Outros");
    const acc = {};
    nomes.forEach(n => { acc[n] = []; });
    filtrados.forEach(p => { const c = p.categoria || "Outros"; (acc[c] = acc[c] || []).push(p); });
    Object.keys(acc).forEach(k => { if (!acc[k].length) delete acc[k]; });
    return acc;
  }, [filtrados, categorias]);

  const add = (p) => setCarrinho(c => ({ ...c, [p.id]: { qtd: (c[p.id]?.qtd || 0) + 1, nome: p.nome, preco: p.preco, produto_id: p.id }}));
  const sub = (id) => setCarrinho(c => {
    const cur = c[id]; if (!cur) return c;
    if (cur.qtd <= 1) { const n = {...c}; delete n[id]; return n; }
    return { ...c, [id]: { ...cur, qtd: cur.qtd - 1 } };
  });
  const remover = (id) => setCarrinho(c => { const n = {...c}; delete n[id]; return n; });

  const itens = Object.entries(carrinho).map(([id, v]) => ({ produto_id: id, nome: v.nome, preco: v.preco, quantidade: v.qtd }));
  const total = itens.reduce((s, i) => s + i.preco * i.quantidade, 0);
  const totalQtd = itens.reduce((s, i) => s + i.quantidade, 0);

  const salvarPessoas = async (n) => {
    setPessoas(n);
    setSalvandoPessoas(true);
    try { await garAxios.patch(`/vaipede/garcom/mesas/${mesa.id}/pessoas`, { capacidade: n }); }
    catch (e) { toast.error("Erro ao atualizar pessoas"); }
    finally { setSalvandoPessoas(false); }
  };

  const enviarComanda = async () => {
    if (itens.length === 0) { toast.error("Adicione itens à comanda"); return; }
    setEnviando(true);
    try {
      await garAxios.post("/vaipede/garcom/pedidos", {
        mesa_id: mesa.id, itens,
        cliente_nome: clienteNome, observacao,
      });
      toast.success("Comanda enviada para a cozinha!");
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
    finally { setEnviando(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-stretch justify-center sm:items-center sm:p-4" onClick={onClose} data-testid="vaipede-garcom-comanda-modal">
      <div
        onClick={e => e.stopPropagation()}
        className="bg-white w-full max-w-2xl sm:rounded-3xl flex flex-col overflow-hidden"
        style={{ height: "100dvh", maxHeight: "100dvh" }}
      >
        {/* Header (shrink-0) */}
        <div className="p-3 border-b border-neutral-100 flex items-center justify-between shrink-0">
          <div className="min-w-0">
            <h2 className="font-black text-base truncate">Nova comanda — {mesa.nome || `Mesa ${mesa.numero}`}</h2>
            <p className="text-[11px] text-neutral-500">Toque nos itens para adicionar</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-neutral-100 rounded-full shrink-0" data-testid="vaipede-garcom-close-modal"><X className="w-5 h-5"/></button>
        </div>

        {/* Cliente + Pessoas (shrink-0) */}
        <div className="p-2 border-b border-neutral-100 grid grid-cols-2 gap-2 shrink-0">
          <input value={clienteNome} onChange={e => setClienteNome(e.target.value)} placeholder="Cliente (opcional)" className="px-3 py-2 rounded-xl border border-neutral-200 text-sm" data-testid="vaipede-garcom-cliente"/>
          <div className="flex items-center gap-2 bg-neutral-50 rounded-xl px-2 py-1">
            <span className="text-[11px] text-neutral-600 shrink-0">Pessoas</span>
            <button onClick={() => salvarPessoas(Math.max(1, pessoas - 1))} className="p-1 rounded-full bg-white border shrink-0" data-testid="vaipede-garcom-pessoas-minus"><Minus className="w-3 h-3"/></button>
            <span className="font-black w-6 text-center">{pessoas}</span>
            <button onClick={() => salvarPessoas(pessoas + 1)} className="p-1 rounded-full bg-white border shrink-0" data-testid="vaipede-garcom-pessoas-plus"><Plus className="w-3 h-3"/></button>
          </div>
        </div>

        {/* Busca (shrink-0) */}
        <div className="px-3 pt-2 shrink-0">
          <div className="flex items-center gap-2 border border-neutral-200 rounded-xl px-3 py-2">
            <Search className="w-4 h-4 text-neutral-400"/>
            <input value={busca} onChange={e => setBusca(e.target.value)} placeholder="Buscar produto..." className="flex-1 outline-none text-sm"/>
          </div>
        </div>

        {/* Produtos (flex-1, único que scrolla) */}
        <div className="flex-1 min-h-0 overflow-y-auto p-3">
          {Object.keys(grouped).length === 0 && (
            <p className="text-center text-neutral-400 text-sm py-6">Sem produtos cadastrados</p>
          )}
          {Object.entries(grouped).map(([cat, arr]) => (
            <div key={cat} className="mb-4">
              <h3 className="font-black text-sm mb-2">{cat}</h3>
              <div className="grid grid-cols-2 gap-2">
                {arr.map(p => {
                  const qtd = carrinho[p.id]?.qtd || 0;
                  return (
                    <button key={p.id} type="button" onClick={() => add(p)} className={`text-left bg-white border-2 rounded-xl p-3 transition ${qtd > 0 ? "border-[#EA1D2C] bg-red-50" : "border-neutral-200 hover:border-neutral-400"}`} data-testid={`vaipede-garcom-prod-${p.id}`}>
                      <div className="font-bold text-sm line-clamp-1">{p.nome}</div>
                      <div className="flex items-center justify-between mt-1">
                        <span className="text-xs text-[#EA1D2C] font-black">{money(p.preco)}</span>
                        {qtd > 0 && <span className="text-xs bg-[#EA1D2C] text-white font-black px-2 py-0.5 rounded-full">×{qtd}</span>}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Bottom bar (shrink-0) — sempre visível */}
        <div className="border-t-2 border-neutral-100 shrink-0 shadow-[0_-4px_12px_rgba(0,0,0,0.06)]">
          {showCart && itens.length > 0 && (
            <div className="max-h-[35vh] overflow-y-auto px-3 py-2 border-b border-neutral-100">
              {itens.map(it => (
                <div key={it.produto_id} className="flex items-center gap-2 py-1.5 text-sm">
                  <span className="flex-1 truncate">{it.nome}</span>
                  <button onClick={() => sub(it.produto_id)} className="p-1 rounded-full bg-neutral-100"><Minus className="w-3 h-3"/></button>
                  <span className="font-black w-6 text-center">{it.quantidade}</span>
                  <button onClick={() => add({ id: it.produto_id, nome: it.nome, preco: it.preco })} className="p-1 rounded-full bg-neutral-100"><Plus className="w-3 h-3"/></button>
                  <span className="font-black w-16 text-right text-xs">{money(it.preco * it.quantidade)}</span>
                  <button onClick={() => remover(it.produto_id)} className="p-1 text-red-500"><Trash2 className="w-3 h-3"/></button>
                </div>
              ))}
              <textarea
                value={observacao}
                onChange={e => setObservacao(e.target.value)}
                rows={2}
                placeholder="Observações (sem cebola, ponto da carne, etc.)"
                className="w-full mt-2 px-3 py-2 rounded-xl border border-neutral-200 text-xs resize-none focus:border-[#EA1D2C] outline-none"
                data-testid="vaipede-garcom-observacao"
              />
            </div>
          )}
          <div className="flex items-center gap-2 p-3">
            {itens.length > 0 && (
              <button onClick={() => setShowCart(v => !v)} className="px-2 py-2 rounded-xl bg-neutral-100 text-neutral-700 text-xs font-bold whitespace-nowrap" data-testid="vaipede-garcom-toggle-cart">
                {showCart ? "Ocultar" : `${totalQtd} item${totalQtd > 1 ? "s" : ""}`}
              </button>
            )}
            <div className="flex-1 min-w-0">
              <div className="text-[10px] text-neutral-500 leading-none">Total</div>
              <div className="font-black text-xl text-[#EA1D2C] leading-none mt-1">{money(total)}</div>
            </div>
            <button
              onClick={enviarComanda}
              disabled={enviando || itens.length === 0}
              className="px-4 py-3 rounded-2xl bg-[#EA1D2C] text-white font-black text-sm hover:bg-[#c8121f] disabled:opacity-40 flex items-center gap-2 whitespace-nowrap"
              data-testid="vaipede-garcom-enviar"
            >
              <Check className="w-4 h-4"/> Enviar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
